import { Info, Cube, Truck, Equals, Calendar, ShoppingCart, TrendUp } from "@phosphor-icons/react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { formatNumber } from "~/utils/formatting"
import { DateFormatType, formatDate } from "~/utils/datetime"
import HoverCardRow from "../reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "../reusable/hovercard/HoverCardRowWrapper"
import { ExpectedReturnsRow } from "./ExpectedReturnsRow"

interface Props {
  projectedStock: number
  stockAtEAD: number
  incomingDuringPeriod: number
  expectedReturns?: number
  orderQuantity: number
  periodDemand: number
  endOfPeriodDate: Date
  isAggregated?: boolean
}

export function ProjectedStockEOPField(props: Props) {
  const { projectedStock } = props
  return (
    <HoverCard>
      <HoverCardTrigger className="flex h-full w-full items-center">
        {formatNumber(projectedStock)}
      </HoverCardTrigger>

      <HoverCardContent className="w-80 p-0" align="start">
        <ProjectedStockEOPFieldContent {...props} />
      </HoverCardContent>
    </HoverCard>
  )
}

export function ProjectedStockEOPFieldContent({
  projectedStock,
  stockAtEAD,
  incomingDuringPeriod,
  expectedReturns = 0,
  orderQuantity,
  periodDemand,
  endOfPeriodDate,
  isAggregated,
}: Props) {
  return (
    <>
      <div className="px-4 py-3 border-b border-base bg-base">
        <div className="flex items-center justify-between">
          <span className="label-base text-fg-base">Projected Stock at End of Period</span>
        </div>
        <div className="text-2xl font-semibold mt-1 text-fg-base">
          {formatNumber(projectedStock)} units
          {isAggregated && <span className="paragraph-sm text-fg-subtle ml-1">(total)</span>}
        </div>
        <p className="paragraph-sm text-fg-subtle mt-1">Stock level at end of purchase period</p>
      </div>

      <HoverCardRowWrapper>
        <HoverCardRow
          icon={<Calendar />}
          label="Period ends"
          value={formatDate(endOfPeriodDate, DateFormatType.FullDate)}
        />

        <hr className="border-t border-dashed border-base" />

        <HoverCardRow
          icon={<Cube />}
          label="Stock at ETA"
          value={`${formatNumber(stockAtEAD)} units`}
        />
        <HoverCardRow
          icon={<Truck />}
          label="Incoming in period"
          value={
            incomingDuringPeriod >= 1
              ? `+${formatNumber(incomingDuringPeriod)} units`
              : `${formatNumber(incomingDuringPeriod)} units`
          }
          className={incomingDuringPeriod >= 1 ? "text-green-500" : ""}
        />
        <ExpectedReturnsRow quantity={expectedReturns} />
        {orderQuantity > 0 && (
          <HoverCardRow
            icon={<ShoppingCart />}
            label="This order"
            value={`+${formatNumber(orderQuantity)} units`}
            className="text-green-500"
          />
        )}
        <HoverCardRow
          icon={<TrendUp />}
          label="Demand in period"
          value={`-${formatNumber(periodDemand)} units`}
          className="text-red-500"
        />
        <hr className="border-t border-dashed border-base" />
        <HoverCardRow
          icon={<Equals />}
          label="End of period stock"
          value={`${formatNumber(projectedStock)} units`}
        />
      </HoverCardRowWrapper>

      <div className="px-4 py-3 border-t border-base bg-subtle flex items-start gap-2 text-fg-subtle">
        <Info className="shrink-0" />
        <p className="paragraph-sm">Projected stock after order and demand</p>
      </div>
    </>
  )
}
